import React, { useId } from 'react';

// NPC tipine göre çizilen, animasyonlu SVG karakter.
// Aynı tipteki NPC'ler isimden türetilen ten / saç / sakal varyasyonlarıyla birbirinden ayrılır.

const FEMALE_NAMES = new Set(['selvi', 'aylin', 'lena', 'gül', 'gul', 'tara', 'naz', 'sahra', 'vera', 'meriç', 'meric']);
const FEMALE_OVERRIDES = new Set(['zırh ustası meriç']); // Meriç kadın usta
const MALE_OVERRIDES = new Set([]);

const SKINS = [
  ['#e2b48f', '#c99370'],
  ['#c98e63', '#ad744c'],
  ['#b0754b', '#93603b'],
  ['#d7a07a', '#b9825d'],
  ['#8e5a38', '#74472b'],
];
const HAIRS = ['#2a1d17', '#4b2e1a', '#6e4526', '#1b1b1f', '#8a5a2e', '#b9b2a6'];

export const NPC_FX = {
  demirci: 'spark',
  market: 'gold',
  silahci: 'ember',
  zirhci: 'spark',
  bahcivan: 'leaf',
  balikci: 'water',
  madenci: 'dust',
};

export const NPC_HALO = {
  demirci: 'rgba(224,102,44,.42)',
  market: 'rgba(212,171,85,.38)',
  silahci: 'rgba(196,72,52,.36)',
  zirhci: 'rgba(150,170,190,.36)',
  bahcivan: 'rgba(120,170,90,.34)',
  balikci: 'rgba(80,150,200,.34)',
  madenci: 'rgba(230,170,80,.34)',
};

// Oyunun NPC sahne görsellerinden kesilmiş gerçekçi karakter portreleri.
// Anahtar: NPC imageUrl dosya adı. Listede olmayan NPC için çizilmiş SVG karakter kullanılır.
const ART_PORTRAITS = {
  demirci: { file: 'demirci', thumb: '62% 2%', light: 'forge' },
  market: { file: 'market', thumb: '46% 16%', light: 'potion' },
  silahci: { file: 'silahci', thumb: '50% 4%', light: 'sun' },
  zirhci: { file: 'zirhci', thumb: '50% 4%', light: 'sun' },
};
// NPC görsel dosyası (stem) -> portre + varyant. Aynı sahne görselini paylaşan NPC'ler
// aynı portreyi kopyalamak yerine ayna/ton varyantıyla ayrışır (yeni asset üretilmez).
// Oyundaki tüm NPC'ler bu portrelerden birini kullanır (çizilmiş SVG yalnızca son çare).
const TONE = {
  korhan: { mirror: true, tone: 'hue-rotate(-16deg) saturate(1.15) brightness(.94)' },
  ruzgar: { mirror: false, tone: 'sepia(.28) hue-rotate(12deg) saturate(1.1) brightness(1.06)' },
  ferhat: { mirror: true, tone: 'hue-rotate(-10deg) saturate(1.04) brightness(.97)' },
  deniz: { mirror: true, tone: 'hue-rotate(20deg) saturate(.9) brightness(1.06) contrast(1.04)' },
  timur: { mirror: true, tone: 'hue-rotate(-14deg) saturate(1.12) brightness(.96)' },
  lena: { mirror: true, tone: 'hue-rotate(14deg) saturate(1.06) brightness(1.03)' },
  aylin: { mirror: false, tone: 'sepia(.22) hue-rotate(40deg) saturate(1.05) brightness(1.04)' },
  tara: { mirror: true, tone: 'hue-rotate(-24deg) saturate(1.1)' },
  naz: { mirror: false, tone: 'sepia(.3) saturate(1.12) brightness(1.02)' },
  sahra: { mirror: true, tone: 'sepia(.35) hue-rotate(-8deg) saturate(1.2) brightness(1.06)' },
  vera: { mirror: false, tone: 'hue-rotate(60deg) saturate(.92) brightness(.98)' },
  gul: { mirror: true, tone: 'hue-rotate(-18deg) saturate(1.1) brightness(1.02)' },
  koy: { mirror: true, tone: 'saturate(.92) brightness(.97) contrast(1.04)' },
};
const ART_BY_IMAGE = {
  // 3-6. köylerdeki isimsiz demirciler genel image/demirci.png görselini kullanıyor
  demirci: { key: 'demirci' },
  'v1-demirci-celatin': { key: 'demirci' },
  'v2-demirci-korhan': { key: 'demirci', ...TONE.korhan },
  'v3-madenci-ruzgar': { key: 'demirci', ...TONE.ruzgar },
  // 3-6. köylerdeki isimsiz NPC'ler genel image/<tip>.png görsellerini kullanıyor
  market: { key: 'market', ...TONE.koy },
  silahci: { key: 'silahci', ...TONE.koy },
  zirhci: { key: 'zirhci', ...TONE.koy },
  'v1-market-selvi': { key: 'market' },
  'v2-market-lena': { key: 'market', ...TONE.lena },
  'v1-bahcivan-aylin': { key: 'market', ...TONE.aylin },
  'v2-bahcivan-tara': { key: 'market', ...TONE.tara },
  'v3-bahcivan-naz': { key: 'market', ...TONE.naz },
  'v4-bahcivan-sahra': { key: 'market', ...TONE.sahra },
  'v5-bahcivan-vera': { key: 'market', ...TONE.vera },
  'v6-bahcivan-timur': { key: 'market', ...TONE.timur },
  'v1-silahci-boran': { key: 'silahci' },
  'v2-silahci-ferhat': { key: 'silahci', ...TONE.ferhat },
  'v2-balikci-deniz': { key: 'silahci', ...TONE.deniz },
  'v1-zirhci-meric': { key: 'zirhci' },
  'v2-zirhci-gul': { key: 'zirhci', ...TONE.gul },
};
// Görseli eşleşmeyen NPC: tipine en yakın portre.
const ART_BY_TYPE = {
  demirci: 'demirci',
  madenci: 'demirci',
  market: 'market',
  bahcivan: 'market',
  silahci: 'silahci',
  balikci: 'silahci',
  zirhci: 'zirhci',
};

const imageStem = (npc) => (npc?.imageUrl || '').split('/').pop().replace(/\.[a-z0-9]+$/i, '');

function artEntry(npc) {
  const byImage = ART_BY_IMAGE[imageStem(npc)];
  if (byImage) return byImage;
  const key = ART_BY_TYPE[npc?.type];
  return key ? { key } : null;
}

/** NPC'nin oyun görselinden kesilmiş portresi (eşleşme yoksa null → çizilmiş karakter). */
export function npcArtPortrait(npc) {
  const entry = artEntry(npc);
  if (!entry) return null;
  const a = ART_PORTRAITS[entry.key];
  return {
    ...a,
    mirror: Boolean(entry.mirror),
    tone: entry.tone || '',
    src: `${process.env.PUBLIC_URL || ''}/image/npcs/portraits/${a.file}.webp`,
  };
}

/**
 * NPC sahne görseli: image/npcs altındaki 1024px PNG'ler (1.4-1.7 MB, 16 dosyada yalnızca 4
 * farklı görsel) yerine aynı görselin küçük webp kopyası. Eşleşme yoksa orijinal imageUrl.
 */
export function npcSceneUrl(npc) {
  const raw = npc?.imageUrl || '';
  const entry = artEntry(npc);
  if (entry) return `${process.env.PUBLIC_URL || ''}/image/npcs/scenes/${entry.key}.webp`;
  return raw ? `/${raw.replace(/^\//, '')}` : '';
}

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function npcLook(npc) {
  const name = (npc?.name || '').trim();
  const lower = name.toLocaleLowerCase('tr');
  const first = lower.split(/\s+/).pop();
  let female = FEMALE_NAMES.has(first);
  if (FEMALE_OVERRIDES.has(lower)) female = true;
  if (MALE_OVERRIDES.has(lower)) female = false;
  const h = hash(lower || 'npc');
  const type = NPC_FX[npc?.type] ? npc.type : 'market';
  return {
    type,
    female,
    skin: SKINS[h % SKINS.length],
    hair: HAIRS[(h >> 3) % (HAIRS.length - (female ? 1 : 0))],
    beard: ['full', 'short', 'mustache'][(h >> 6) % 3],
    bald: type === 'demirci' && !female,
  };
}

/* ---------- parçalar ---------- */

const Torso = ({ fill, shade }) => (
  <>
    <path d="M28 400 C38 300 84 266 150 260 C216 266 262 300 272 400Z" fill={fill} />
    <path d="M28 400 C38 300 84 266 150 260 C120 290 104 340 100 400Z" fill={shade} opacity=".35" />
  </>
);

function Outfit({ type, female, g }) {
  switch (type) {
    case 'demirci':
      return (
        <>
          <Torso fill="#4a4f57" shade="#2b2e33" />
          <path d="M92 300 L208 300 L222 400 L78 400Z" fill={`url(#${g}-leather)`} />
          <path d="M92 300 L80 270 M208 300 L220 270" stroke="#3f2414" strokeWidth="7" strokeLinecap="round" />
          <rect x="118" y="342" width="64" height="28" rx="3" fill="#5a3620" stroke="#3f2414" strokeWidth="2" />
          <path d="M60 300 C40 330 40 370 52 400 L96 400 C88 360 92 330 100 310Z" fill={`url(#${g}-skin)`} />
        </>
      );
    case 'market':
      return (
        <>
          <Torso fill="#7a2330" shade="#3d0f17" />
          <path d="M150 264 L118 400 L182 400Z" fill="#b8913f" />
          <path d="M150 264 L136 400 M150 264 L164 400" stroke="#6b1d29" strokeWidth="3" />
          <path d="M58 336 Q150 352 242 336 L245 356 Q150 372 55 356Z" fill="#2f5d50" />
          <circle cx="150" cy="346" r="7" fill="#d4ab55" />
        </>
      );
    case 'silahci':
      return (
        <>
          <path d="M226 210 L262 400" stroke="#2e2b2a" strokeWidth="10" />
          <rect x="214" y="188" width="26" height="8" rx="2" fill="#c9a24a" transform="rotate(-20 227 192)" />
          <Torso fill="#5b3a26" shade="#2e1c11" />
          <path d="M70 290 L230 380" stroke="#2a1a10" strokeWidth="16" />
          <path d="M70 290 L230 380" stroke="#c9a24a" strokeWidth="2" strokeDasharray="4 10" />
          <path d="M112 262 Q150 292 188 262 L196 280 Q150 312 104 280Z" fill="#8e2b20" />
        </>
      );
    case 'zirhci':
      return (
        <>
          <Torso fill="#3d4148" shade="#1f2226" />
          <path d="M96 292 Q150 276 204 292 L212 400 L88 400Z" fill={`url(#${g}-steel)`} />
          <path d="M150 290 L150 400" stroke="#5d6672" strokeWidth="3" />
          <path d="M110 330 Q150 318 190 330 M106 362 Q150 350 194 362" stroke="#5d6672" strokeWidth="3" fill="none" />
          <ellipse cx="74" cy="300" rx="40" ry="26" fill={`url(#${g}-steel)`} stroke="#4a525c" strokeWidth="3" />
          <ellipse cx="226" cy="300" rx="40" ry="26" fill={`url(#${g}-steel)`} stroke="#4a525c" strokeWidth="3" />
          <circle cx="74" cy="300" r="4" fill="#c9a24a" /><circle cx="226" cy="300" r="4" fill="#c9a24a" />
        </>
      );
    case 'bahcivan':
      return (
        <>
          <Torso fill={female ? '#8a6a3c' : '#5f7d3f'} shade="#2f3d1e" />
          <path d="M104 300 L196 300 L206 400 L94 400Z" fill="#6b5a3a" />
          <path d="M104 300 L96 268 M196 300 L204 268" stroke="#4f4129" strokeWidth="8" strokeLinecap="round" />
          <circle cx="114" cy="306" r="5" fill="#c9a24a" /><circle cx="186" cy="306" r="5" fill="#c9a24a" />
          <rect x="128" y="330" width="44" height="26" rx="3" fill="#57482d" />
        </>
      );
    case 'balikci':
      return (
        <>
          <Torso fill="#2f4f6b" shade="#152636" />
          <path d="M100 280 L130 400 M200 280 L170 400" stroke="#d8cfb8" strokeWidth="4" opacity=".7" />
          <path d="M96 300 L130 400 L92 400Z M204 300 L170 400 L208 400Z" fill="#4a5f4a" />
          <path d="M60 360 q20 -10 40 0 q20 10 40 0 q20 -10 40 0 q20 10 40 0 q20 -10 40 0" stroke="#c9b993" strokeWidth="2" fill="none" opacity=".6" />
        </>
      );
    case 'madenci':
    default:
      return (
        <>
          <Torso fill="#5a4a3a" shade="#2b2219" />
          <path d="M108 296 L192 296 L200 400 L100 400Z" fill="#3e342a" />
          <ellipse cx="128" cy="330" rx="14" ry="7" fill="#1f1a16" opacity=".5" />
          <ellipse cx="176" cy="360" rx="10" ry="5" fill="#1f1a16" opacity=".45" />
          <path d="M88 296 Q150 282 212 296" stroke="#c98f3c" strokeWidth="6" fill="none" />
        </>
      );
  }
}

function HandItem({ type, g }) {
  switch (type) {
    case 'demirci':
      return (
        <g transform="translate(224 300)">
          <g className="npcp-harm">
            <path d="M-14 0 L10 -64 L34 -56 L16 8Z" fill={`url(#${g}-skin)`} />
            <circle cx="24" cy="-66" r="15" fill={`url(#${g}-skin)`} />
            <path d="M24 -66 L30 -132" stroke="#5a3a22" strokeWidth="8" strokeLinecap="round" />
            <g transform="rotate(6 31 -139)">
              <rect x="6" y="-152" width="50" height="26" rx="3" fill="#55575e" />
              <rect x="6" y="-152" width="50" height="6" rx="2" fill="#8a8d96" />
            </g>
          </g>
        </g>
      );
    case 'market':
      return (
        <>
          <ellipse cx="228" cy="332" rx="18" ry="14" fill={`url(#${g}-skin)`} />
          <ellipse className="npcp-coin" cx="232" cy="310" rx="12" ry="12" fill="#e2b84a" stroke="#9c7a24" strokeWidth="2" />
        </>
      );
    case 'silahci':
      return (
        <g className="npcp-blade">
          <path d="M238 380 L246 160 L254 150 L262 160 L254 380Z" fill={`url(#${g}-steel)`} />
          <path d="M250 162 L250 378" stroke="#e9eef2" strokeWidth="1.5" opacity=".7" />
          <rect x="226" y="376" width="52" height="10" rx="3" fill="#c9a24a" />
          <ellipse cx="252" cy="390" rx="16" ry="12" fill={`url(#${g}-skin)`} />
          <path className="npcp-glint" d="M252 170 l4 10 l10 4 l-10 4 l-4 10 l-4 -10 l-10 -4 l10 -4z" fill="#fff6dc" />
        </g>
      );
    case 'zirhci':
      return (
        <g className="npcp-sway">
          <path d="M18 330 L70 318 L76 380 C70 400 40 404 22 392Z" fill="#6b4228" stroke="#c9a24a" strokeWidth="4" />
          <path d="M46 326 L48 396" stroke="#c9a24a" strokeWidth="3" />
        </g>
      );
    case 'bahcivan':
      return (
        <g>
          <path d="M206 360 L254 360 L248 396 L212 396Z" fill="#9a5a34" />
          <g className="npcp-leaf">
            <path d="M230 360 C230 340 232 326 236 312" stroke="#4f7a32" strokeWidth="4" fill="none" />
            <path d="M234 330 C214 322 208 306 216 300 C230 304 236 318 234 330Z" fill="#6fa047" />
            <path d="M236 316 C252 306 266 310 266 318 C256 326 244 324 236 316Z" fill="#83b85a" />
          </g>
          <ellipse cx="206" cy="372" rx="14" ry="12" fill={`url(#${g}-skin)`} />
        </g>
      );
    case 'balikci':
      return (
        <g>
          <g className="npcp-rod">
            <path d="M232 380 L292 70" stroke="#6b4a2a" strokeWidth="6" strokeLinecap="round" />
            <path className="npcp-line" d="M292 70 Q300 180 286 260" stroke="#d9d4c7" strokeWidth="1.2" fill="none" />
            <circle cx="286" cy="262" r="4" fill="#d65a4a" />
          </g>
          <ellipse cx="236" cy="372" rx="16" ry="13" fill={`url(#${g}-skin)`} />
        </g>
      );
    case 'madenci':
    default:
      return (
        <g className="npcp-sway">
          <path d="M70 380 L240 220" stroke="#6b4a2a" strokeWidth="8" strokeLinecap="round" />
          <path d="M206 196 Q244 206 270 252 L262 258 Q240 222 214 212Z" fill="#6f747d" />
          <path d="M206 196 Q190 214 192 236 L200 234 Q202 216 214 206Z" fill="#6f747d" />
          <ellipse cx="96" cy="358" rx="16" ry="13" fill={`url(#${g}-skin)`} />
        </g>
      );
  }
}

function Headwear({ type, female, bald, hairFill, g }) {
  switch (type) {
    case 'demirci':
      return female ? (
        <path d="M98 150 C130 128 170 128 202 150 L204 166 C170 148 130 148 96 166Z" fill="#8e2b20" />
      ) : (
        <>
          <path d="M120 142 Q150 128 180 142" stroke="#fff" strokeWidth="5" fill="none" opacity=".16" strokeLinecap="round" />
          <path d="M98 150 C130 140 170 140 202 150 L202 164 C170 154 130 154 98 164Z" fill="#8e2b20" />
          <path d="M196 156 l18 10 -6 4 z" fill="#8e2b20" />
        </>
      );
    case 'market':
      return (
        <>
          <path d="M96 160 C92 102 128 82 150 82 C176 82 210 102 204 160 C186 144 114 144 96 160Z" fill="#ece0c2" />
          <path d="M99 146 C128 126 174 126 202 146 M104 126 C132 110 170 110 196 126" stroke="#c9b993" strokeWidth="5" fill="none" />
          <path d="M150 120 C146 98 158 84 170 70" stroke="#d4ab55" strokeWidth="3" fill="none" />
          <circle cx="150" cy="128" r="8" fill="#2f8a6f" stroke="#d4ab55" strokeWidth="3" />
        </>
      );
    case 'bahcivan':
      return (
        <>
          <path d="M112 132 C112 96 188 96 188 132Z" fill="#c9a65a" />
          <path d="M112 128 L188 128 L188 136 L112 136Z" fill="#6f8f3f" />
          <ellipse cx="150" cy="136" rx="92" ry="17" fill="#d8b766" />
          <path d="M70 136 Q150 152 230 136" stroke="#a9863f" strokeWidth="2" fill="none" />
        </>
      );
    case 'balikci':
      return (
        <>
          <path d="M100 154 C96 98 130 84 150 84 C172 84 206 98 202 154Z" fill="#7b2f2f" />
          <path d="M98 146 L204 146 L204 162 L98 162Z" fill="#5e2222" rx="4" />
          <path d="M110 120 L190 120 M106 134 L196 134" stroke="#94403e" strokeWidth="3" />
          <circle cx="150" cy="82" r="9" fill="#94403e" />
        </>
      );
    case 'madenci':
      return (
        <>
          <path d="M98 156 C96 100 130 86 150 86 C172 86 206 100 204 156Z" fill="#b48a2e" />
          <path d="M88 156 L212 156 L212 166 L88 166Z" fill="#8d6a1f" />
          <rect x="138" y="104" width="24" height="22" rx="4" fill="#3a3530" />
          <circle className="npcp-lamp" cx="150" cy="115" r="7" fill="#ffe08a" />
          <circle className="npcp-lamp" cx="150" cy="115" r="18" fill="#ffd36a" opacity=".25" />
        </>
      );
    case 'silahci':
    case 'zirhci':
    default:
      if (female || bald) return null;
      return (
        <path d="M100 168 C96 116 128 98 150 98 C176 98 206 116 200 168 C190 146 174 136 150 136 C126 136 110 146 100 168Z" fill={hairFill} />
      );
  }
}

const NpcPortrait = ({ npc, className = '', viewBox = '0 0 300 400' }) => {
  const rid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const g = `np${rid}`;
  const look = npcLook(npc);
  const { type, female, skin, hair, beard, bald } = look;
  const hairFill = `url(#${g}-hair)`;

  return (
    <svg
      className={`npcp npcp--${type} ${className}`}
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={npc?.name || 'NPC'}
    >
      <defs>
        <linearGradient id={`${g}-skin`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={skin[0]} />
          <stop offset="1" stopColor={skin[1]} />
        </linearGradient>
        <radialGradient id={`${g}-face`} cx=".38" cy=".38" r=".75">
          <stop offset="0" stopColor={skin[0]} />
          <stop offset=".75" stopColor={skin[1]} />
          <stop offset="1" stopColor={skin[1]} />
        </radialGradient>
        <linearGradient id={`${g}-hair`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hair} stopOpacity=".85" />
          <stop offset="1" stopColor={hair} />
        </linearGradient>
        <linearGradient id={`${g}-leather`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#7a4c2e" />
          <stop offset="1" stopColor="#4e2f1b" />
        </linearGradient>
        <linearGradient id={`${g}-steel`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#c7d0d8" />
          <stop offset=".5" stopColor="#7d8893" />
          <stop offset="1" stopColor="#4a525c" />
        </linearGradient>
      </defs>

      <g className="npcp-breathe">
        {female ? (
          <path
            d="M94 180 C88 108 124 90 150 90 C178 90 214 108 206 180 L218 310 C190 324 110 324 82 310Z"
            fill={hairFill}
          />
        ) : null}

        <Outfit type={type} female={female} g={g} />

        <g className="npcp-head">
          <rect x="132" y="222" width="36" height="46" rx="8" fill={skin[1]} />
          <ellipse cx="102" cy="190" rx="9" ry="14" fill={skin[1]} />
          <ellipse cx="198" cy="190" rx="9" ry="14" fill={skin[1]} />
          <ellipse cx="150" cy="185" rx={female ? 45 : 48} ry={female ? 55 : 58} fill={`url(#${g}-face)`} />

          {/* yüz */}
          <path d="M150 172 L144 199 Q150 203 156 199Z" fill={skin[1]} />
          <ellipse className="npcp-eye" cx="131" cy="178" rx="5.5" ry="4.5" fill="#1a1210" />
          <ellipse className="npcp-eye" cx="169" cy="178" rx="5.5" ry="4.5" fill="#1a1210" />
          <circle cx="133" cy="176.5" r="1.4" fill="#fff" opacity=".7" />
          <circle cx="171" cy="176.5" r="1.4" fill="#fff" opacity=".7" />
          {female ? (
            <>
              <path d="M123 174 q8 -6 16 -1 M161 173 q8 -5 16 1" stroke="#1a1210" strokeWidth="2.5" fill="none" strokeLinecap="round" />
              <path d="M122 164 q10 -6 21 -2 M157 162 q11 -4 21 2" stroke={hair} strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M138 214 Q150 222 162 214 Q150 218 138 214Z" fill="#a8564a" />
              <ellipse cx="124" cy="200" rx="9" ry="5" fill="#d9766a" opacity=".22" />
              <ellipse cx="176" cy="200" rx="9" ry="5" fill="#d9766a" opacity=".22" />
            </>
          ) : (
            <>
              <path
                d={type === 'demirci' ? 'M114 168 L144 171 M156 171 L186 168' : 'M119 165 q12 -8 25 -1 M156 164 q13 -7 25 1'}
                stroke={bald ? '#3b2210' : hair}
                strokeWidth={type === 'demirci' ? 7 : 4.5}
                fill="none"
                strokeLinecap="round"
              />
              {beard === 'full' ? (
                <path className="npcp-beard" d="M102 190 C100 262 130 296 150 300 C170 296 200 262 198 190 C188 216 170 224 150 224 C130 224 112 216 102 190Z" fill={hairFill} />
              ) : beard === 'short' ? (
                <path className="npcp-beard" d="M106 196 C108 246 132 264 150 266 C168 264 192 246 194 196 C184 216 168 222 150 222 C132 222 116 216 106 196Z" fill={hairFill} />
              ) : (
                <path d="M142 220 Q150 226 158 220" stroke="#5a2f22" strokeWidth="3" fill="none" strokeLinecap="round" />
              )}
              <path d="M124 210 C140 199 160 199 176 210 C162 216 156 212 150 207 C144 212 138 216 124 210Z" fill={hair} />
            </>
          )}
          {type === 'silahci' && !female ? (
            <path d="M170 160 L182 196" stroke="#8a3b2e" strokeWidth="3" strokeLinecap="round" opacity=".8" />
          ) : null}
          {type === 'madenci' ? <ellipse cx="174" cy="204" rx="10" ry="5" fill="#2a2420" opacity=".4" /> : null}

          {female ? (
            <path
              d="M100 172 C98 118 128 96 150 96 C178 96 204 118 200 172 C188 140 160 126 138 134 C122 140 108 152 100 172Z"
              fill={hairFill}
            />
          ) : null}
          <Headwear type={type} female={female} bald={bald} hairFill={hairFill} g={g} />
        </g>

        <HandItem type={type} g={g} />
      </g>
    </svg>
  );
};

export default NpcPortrait;
